import { Suspense, lazy, useEffect } from 'react'
import {
  Navigate,
  Routes,
  Route,
  useNavigate,
  useParams,
} from 'react-router-dom'
import { CircularProgress, Container, Stack } from '@mui/material'
import { useStytchIsAuthorized } from '@stytch/react/b2b'
import { useFeatureFlagContext } from '@/feature_flags/FeatureFlagProvider'
import EmailUploadsPage from '@/pages/EmailUploadsPage'
import useIsSuperUser from '@/services/hooks/useIsSuperUser'
import AppLayout from '@/components/app-layout/AppLayout'
import AppTopBar from '@/components/app-top-bar/AppTopBar'
import ConnectedApp from '@/components/auth/ConnectedApp'
import BreadcrumbPortalProvider from '@/components/breadcrumbs/BreadcrumbPortalProvider'
import CredentialAuthenticatedPage from '@/components/credentials/CredentialAuthenticatedPage'
import DashboardRouting from '@/components/dashboard-routing/DashboardRouting'
import DataListList from '@/components/data-lists/DataListList'
import DataListPage from '@/components/data-lists/DataListPage'
import DocumentsProcessed from '@/components/data-visualization/DocumentsProcessed'
import DrawerPortalProvider from '@/components/drawer/DrawerPortalProvider'
import IntegrationPage from '@/components/integration-page/IntegrationPage'
import ModelLibrary from '@/components/model-library/ModelLibrary'
import ModelPage from '@/components/model-library/ModelPage'
import ModelSettings from '@/components/model-page/ModelConfiguration'
import NavigationRailPortalProvider from '@/components/navigation-rail/NavigationRailPortalProvider'
import DocumentRulesPage from '@/components/project-settings/document-rules/DocumentRulesPage'
import FieldRulesPage from '@/components/project-settings/field-rules/FieldRulesPage'
import FieldConfiguration from '@/components/project-settings/FieldConfiguration'
import ModelsTablePage from '@/components/project-settings/models/ModelsTablePage'
import ProjectFeaturesPage from '@/components/project-settings/ProjectFeaturesPage'
import ProjectSettings from '@/components/project-settings/ProjectSettings'
import ProjectSettingsLayout from '@/components/project-settings/ProjectSettingsLayout'
import ProjectTemplateSettings from '@/components/project-settings/ProjectTemplateSettings'
import PublishedModelManagementView from '@/components/project-settings/PublishedModelManagementView'
import PurchaseOrderMatchingPage from '@/components/project-settings/purchase-orders/PurchaseOrderMatchingPage'
import ProjectLayout from '@/components/project-tables/ProjectLayout'
import ProjectTables from '@/components/project-tables/ProjectTables'
import ProjectTablesRouting from '@/components/project-tables/ProjectTablesRouting'
import ProjectWizardPage from '@/components/project-wizard/ProjectWizardPage'
import RelevantIDsProvider from '@/components/relevant-ids/RelevantIDsProvider'
import TagsPage from '@/components/tags-page/TagsPage'
import TopBarPortalProvider from '@/components/top-bar-portal/TopBarPortalProvider'
import TotalDocumentsPage from '@/components/total-documents-page/TotalDocumentsPage'
import FileUploadProvider from '@/components/upload-files/FileUploadProvider'
import UserPage from '@/components/users/UserPage'
import UsersTable from '@/components/users/UsersTable'
import WorkflowStatesPage from '@/components/workflow-states-page/WorkflowStatesPage'
import AgreementsPage from './AgreementsPage'
import CustomVariablesPage from './CustomVariablesPage'
import FeatureFlagsPage from './FeatureFlagsPage'
import GlobalSettingsPage from './GlobalSettingsPage'
import InFlightDocsPage from './InFlightDocsPage'
import ListsPage from './ListsPage'
import MetricsPage from './MetricsPage'
import NotFound from './NotFound'
import OpenSourcePage from './OpenSourcePage'
import OrganizationsPage from './OrganizationsPage'
import OrganizationTypesPage from './OrganizationTypesPage'
import PreferencesPage from './PreferencesPage'
import ProfilePage from './ProfilePage'
import ProjectsPage from './ProjectsPage'
import TermsOfUsePage from './TermsOfUsePage'
import UsersPage from './UsersPage'
import VerifyEmailPage from './VerifyEmailPage'

const AdvancedSettings = lazy(
  () =>
    import('@/components/project-settings/advanced-settings/AdvancedSettings'),
)
const ZerapixManagementPage = lazy(
  () => import('@/components/zerapix-management/ZerapixManagementPage'),
)
const CustomersPage = lazy(
  () => import('@/components/zerapix-management/CustomersPage'),
)
const CustomerPage = lazy(
  () => import('@/components/zerapix-management/CustomerPage'),
)
const BillingPage = lazy(() => import('./BillingPage'))

function OldValidationRedirect() {
  const { projectId, documentId } = useParams()
  return (
    <Navigate
      to={`/projects/${projectId}/documents?document_id=${documentId}`}
      replace
    />
  )
}

function DashboardRedirect() {
  const { projectId } = useParams()
  const navigate = useNavigate()
  useEffect(() => {
    const id = setTimeout(() => {
      // For some reason, just navigating through this call or using Navigate doesn't work, but using navigate with replace does work.
      navigate(`/projects/${projectId}/metrics`, { replace: true })
    }, 0)
    return () => clearTimeout(id)
  }, [navigate, projectId])
  return null
}

export default function Application() {
  const featureFlags = useFeatureFlagContext()
  const isSuperUser = useIsSuperUser()

  const { isAuthorized: canEditBilling } = useStytchIsAuthorized(
    'pd.billing',
    'edit',
  )

  const { isAuthorized: canEditUser } = useStytchIsAuthorized('pd.user', 'edit')

  return (
    <NavigationRailPortalProvider>
      <DrawerPortalProvider>
        <TopBarPortalProvider>
          <BreadcrumbPortalProvider>
            <RelevantIDsProvider>
              <AppLayout header={<AppTopBar />}>
                <Suspense
                  fallback={
                    <Stack alignItems="center" sx={{ p: 4 }}>
                      <CircularProgress />
                    </Stack>
                  }
                >
                  <Routes>
                    <Route path="oauth2/authorize" element={<ConnectedApp />} />
                    <Route
                      path="projects"
                      element={
                        <FileUploadProvider>
                          <ProjectsPage />
                        </FileUploadProvider>
                      }
                    />

                    <Route
                      path="project-setup/*"
                      element={<ProjectWizardPage />}
                    />

                    <Route
                      path="projects/:projectId"
                      element={<ProjectLayout />}
                    >
                      <Route path="metrics" element={<DashboardRouting />} />
                      <Route path="model" element={<DashboardRouting />} />
                      <Route path="dashboard" element={<DashboardRedirect />} />

                      <Route
                        path="validation/:documentId"
                        element={<OldValidationRedirect />}
                      />

                      <Route path=":view" element={<ProjectTablesRouting />}>
                        <Route index element={<ProjectTables />} />
                      </Route>

                      <Route
                        path="settings"
                        element={<ProjectSettingsLayout />}
                      >
                        <Route
                          index
                          element={<Navigate to="general" replace />}
                        />
                        <Route path="general" element={<ProjectSettings />} />

                        {isSuperUser && (
                          <Route
                            path="features"
                            element={<ProjectFeaturesPage />}
                          />
                        )}

                        <Route
                          path="model-library/:id"
                          element={<ModelPage />}
                        />

                        <Route path="fields" element={<FieldConfiguration />} />
                        <Route
                          path="model-library"
                          element={<ModelLibrary />}
                        />

                        <Route
                          path="model-tables"
                          element={<ModelsTablePage />}
                        />

                        <Route path="tags" element={<TagsPage />} />
                        <Route
                          path="purchase-orders"
                          element={<PurchaseOrderMatchingPage />}
                        />
                        {isSuperUser && (
                          <Route
                            path="template"
                            element={<ProjectTemplateSettings />}
                          />
                        )}
                        {isSuperUser && (
                          <Route
                            path="advanced-settings"
                            element={<AdvancedSettings />}
                          />
                        )}

                        <Route
                          path="document-rules"
                          element={<DocumentRulesPage />}
                        />
                        <Route
                          path="field-rules"
                          element={<FieldRulesPage />}
                        />

                        <Route
                          path="workflow-states/*"
                          element={<WorkflowStatesPage />}
                        />

                        <Route
                          path="model-configuration"
                          element={<ModelSettings />}
                        />

                        {isSuperUser && (
                          <Route
                            path="published-model-management"
                            element={<PublishedModelManagementView />}
                          />
                        )}
                      </Route>

                      <Route path="*" element={<NotFound />} />
                    </Route>

                    <Route path="settings" element={<GlobalSettingsPage />}>
                      <Route
                        index
                        element={<Navigate to="my-profile" replace />}
                      />

                      <Route path="my-profile" element={<ProfilePage />} />
                      <Route
                        path="my-preferences"
                        element={<PreferencesPage />}
                      />

                      {/* <Route path="teams" element={<TeamsPage />}>
                        <Route index element={<TeamsTable />} />
                        <Route path=":id" element={<TeamPage />} />
                      </Route> */}

                      {canEditUser && (
                        <Route path="users" element={<UsersPage />}>
                          <Route index element={<UsersTable />} />
                          <Route path=":id" element={<UserPage />} />
                        </Route>
                      )}

                      <Route
                        path="organizations/*"
                        element={<OrganizationsPage />}
                      />

                      <Route
                        path="organization-types"
                        element={<OrganizationTypesPage />}
                      />

                      <Route
                        path="email-uploads"
                        element={<EmailUploadsPage />}
                      />

                      <Route
                        path="custom-variables/*"
                        element={<CustomVariablesPage />}
                      />

                      <Route
                        path="integrations"
                        element={<Navigate to="../my-profile" />}
                      />
                      <Route
                        path="integrations/:id"
                        element={<IntegrationPage />}
                      />

                      <Route path="lists" element={<ListsPage />}>
                        <Route index element={<DataListList />} />
                        <Route path=":id" element={<DataListPage />} />
                      </Route>

                      {featureFlags.customer_page_visible && (
                        <Route path="customer/:id" element={<CustomerPage />} />
                      )}

                      {featureFlags.show_billing_page && canEditBilling && (
                        <Route path="billing" element={<BillingPage />} />
                      )}
                    </Route>

                    <Route
                      path="credential-authenticated"
                      element={<CredentialAuthenticatedPage />}
                    />

                    <Route path="agreements" element={<AgreementsPage />} />
                    <Route
                      path="open-source-licenses"
                      element={<OpenSourcePage />}
                    />
                    <Route path="terms-of-use" element={<TermsOfUsePage />} />

                    <Route path="verify-email" element={<VerifyEmailPage />} />

                    <Route path="metrics" element={<MetricsPage />}>
                      <Route index element={<Navigate to="documents" />} />
                      <Route
                        path="documents"
                        element={
                          <Container sx={{ py: 4 }}>
                            <DocumentsProcessed />
                          </Container>
                        }
                      />
                    </Route>

                    {isSuperUser && (
                      <Route
                        path="zerapix-management"
                        element={<ZerapixManagementPage />}
                      >
                        <Route index element={<Navigate to="customers" />} />
                        <Route path="customers" element={<CustomersPage />} />
                        <Route
                          path="customers/:id"
                          element={<CustomerPage showBackButton />}
                        />
                        <Route
                          path="feature-flags"
                          element={<FeatureFlagsPage />}
                        />
                      </Route>
                    )}

                    {isSuperUser && (
                      <Route
                        path="total-documents"
                        element={<TotalDocumentsPage />}
                      />
                    )}

                    {isSuperUser && (
                      <Route
                        path="in-flight-docs"
                        element={<InFlightDocsPage />}
                      />
                    )}

                    <Route
                      path="/"
                      element={<Navigate to="projects" replace />}
                    />

                    <Route path="*" element={<NotFound />} />
                  </Routes>
                </Suspense>
              </AppLayout>
            </RelevantIDsProvider>
          </BreadcrumbPortalProvider>
        </TopBarPortalProvider>
      </DrawerPortalProvider>
    </NavigationRailPortalProvider>
  )
}
